import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { DOC_TYPES, formatBRL, STATUS_STYLES } from "@/lib/constants";
import { Bell, MessageCircle, CalendarClock, Loader2, Wallet, CheckCircle2, Mail } from "lucide-react";
import { FollowUps } from "@/components/FollowUps";

const parseDate = (s) => {
  if (!s) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return new Date(s + "T00:00:00");
  const m = s.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (m) return new Date(`${m[3]}-${m[2]}-${m[1]}T00:00:00`);
  const d = new Date(s);
  return isNaN(d) ? null : d;
};

const statusOf = (date) => {
  const d = parseDate(date);
  if (!d) return { label: "Sem data", tone: "bg-slate-100 text-slate-600", diff: 99999 };
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const diff = Math.round((d - today) / 86400000);
  if (diff < 0) return { label: `Atrasado ${Math.abs(diff)}d`, tone: "bg-red-100 text-red-700", diff };
  if (diff === 0) return { label: "Hoje", tone: "bg-emerald-100 text-emerald-700", diff };
  if (diff <= 7) return { label: `Em ${diff}d`, tone: "bg-amber-100 text-amber-700", diff };
  return { label: d.toLocaleDateString("pt-BR"), tone: "bg-sky-100 text-sky-700", diff };
};

function WaButton({ onClick, disabled, testid }) {
  if (disabled) return <Button size="sm" variant="outline" disabled title="Cliente sem WhatsApp" data-testid={testid ? `${testid}-disabled` : undefined}><MessageCircle className="h-4 w-4" /></Button>;
  return <Button size="sm" className="bg-[#25D366] hover:bg-[#1da851]" onClick={onClick} data-testid={testid}><MessageCircle className="h-4 w-4" /></Button>;
}

// Reusable editable-message dialog. Opens WhatsApp with the (edited) text on send.
function useWaSender() {
  const [open, setOpen] = useState(false);
  const [msg, setMsg] = useState("");
  const [wa, setWa] = useState("");
  const trigger = (whatsapp, defaultMsg) => { setWa((whatsapp || "").replace(/\D/g, "")); setMsg(defaultMsg); setOpen(true); };
  const send = () => {
    if (!wa) { toast.error("Cliente sem WhatsApp"); return; }
    window.open(`https://wa.me/${wa}?text=${encodeURIComponent(msg)}`, "_blank");
    setOpen(false);
  };
  const dialog = (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Editar mensagem</DialogTitle><DialogDescription>Personalize o texto antes de enviar ao cliente no WhatsApp.</DialogDescription></DialogHeader>
        <Textarea value={msg} onChange={(e) => setMsg(e.target.value)} rows={6} className="mt-1" data-testid="wa-message-input" />
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
          <Button className="bg-[#25D366] hover:bg-[#1da851]" onClick={send} data-testid="wa-send-btn"><MessageCircle className="h-4 w-4 mr-1" /> Enviar no WhatsApp</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
  return { trigger, dialog };
}

function Retornos() {
  const [items, setItems] = useState(null);
  const [company, setCompany] = useState(null);
  const wa = useWaSender();
  useEffect(() => { Promise.all([api.get("/reminders"), api.get("/company")]).then(([r, c]) => { setItems(r.data); setCompany(c.data); }); }, []);
  if (!items) return <div className="h-40 flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;

  const enriched = items.map((r) => ({ ...r, st: statusOf(r.date) })).sort((a, b) => a.st.diff - b.st.diff);
  const due = enriched.filter((r) => r.st.diff <= 7);
  const defaultMsg = (r) => {
    const d = parseDate(r.date);
    const dstr = d ? d.toLocaleDateString("pt-BR") : "";
    const t = company?.msg_return;
    if (t) return t.replaceAll("{cliente}", r.client_name).replaceAll("{numero}", r.number).replaceAll("{data}", dstr);
    return `Olá ${r.client_name}! 😊 Passando para lembrar do seu retorno agendado para ${dstr}. Podemos confirmar? Qualquer coisa, é só chamar!`;
  };

  return (
    <div className="space-y-3">
      {wa.dialog}
      {due.length > 0 && (
        <Card className="p-4 bg-amber-50 dark:bg-amber-500/10 border-amber-200 dark:border-amber-500/30">
          <p className="text-sm font-semibold text-amber-800 dark:text-amber-300">{due.length} retorno(s) para hoje/atrasados/próximos 7 dias</p>
        </Card>
      )}
      {enriched.length === 0 ? (
        <Card className="p-10 text-center text-muted-foreground"><CalendarClock className="h-10 w-10 mx-auto mb-2 opacity-40" /><p>Nenhum retorno agendado. Preencha "Próximo agendamento" nos documentos de salão/estética.</p></Card>
      ) : (
        <div className="space-y-2">
          {enriched.map((r) => (
            <Card key={r.doc_id} className="p-4 flex items-center gap-3" data-testid={`reminder-${r.doc_id}`}>
              <div className="min-w-0 flex-1">
                <p className="font-semibold truncate">{r.client_name}</p>
                <p className="text-xs text-muted-foreground truncate">{DOC_TYPES[r.type]?.label} {r.number}{r.service ? ` • ${r.service}` : ""}</p>
              </div>
              <Badge variant="outline" className={`text-[10px] ${r.st.tone}`}>{r.st.label}</Badge>
              <WaButton disabled={!(r.client_whatsapp || "").replace(/\D/g, "")} onClick={() => wa.trigger(r.client_whatsapp, defaultMsg(r))} testid={`reminder-wa-${r.doc_id}`} />
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function Cobrancas() {
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState("");
  const wa = useWaSender();
  const [company, setCompany] = useState(null);
  const load = () => Promise.all([api.get("/billing-reminders"), api.get("/company")]).then(([b, c]) => { setItems(b.data); setCompany(c.data); });
  useEffect(() => { load(); }, []);
  if (!items) return <div className="h-40 flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;

  const totalPending = items.reduce((s, r) => s + Number(r.total || 0), 0);
  const enriched = items.map((r) => ({ ...r, st: statusOf(r.date) })).sort((a, b) => a.st.diff - b.st.diff);
  const vencText = (st) => st.diff === 0 ? "vence hoje" : st.diff === 1 ? "vence amanhã" : (st.diff > 1 && st.diff <= 7) ? `vence em ${st.diff} dias` : (st.diff < 0 && st.diff > -9999) ? `venceu há ${Math.abs(st.diff)} dia(s)` : "";
  const defaultMsg = (r) => {
    const st = statusOf(r.date);
    const venc = vencText(st);
    const link = r.share_token ? ` Documento: ${window.location.origin}/doc/${r.share_token}` : "";
    const t = company?.msg_billing;
    if (t) {
      let msg = t.replaceAll("{cliente}", r.client_name).replaceAll("{numero}", r.number).replaceAll("{valor}", formatBRL(r.total)).replaceAll("{vencimento}", venc);
      if (!t.includes("{vencimento}") && venc) msg += ` (${venc})`;
      return msg + link;
    }
    if (st.diff === 0) return `Olá ${r.client_name}! 😊 Lembrete: o pagamento do documento ${r.number} (${formatBRL(r.total)}) vence hoje. Qualquer dúvida, é só chamar!${link}`;
    if (st.diff > 0 && st.diff <= 7) { const quando = st.diff === 1 ? "amanhã" : `em ${st.diff} dias`; return `Olá ${r.client_name}! 😊 Passando para avisar que o pagamento do documento ${r.number} (${formatBRL(r.total)}) vence ${quando} (${parseDate(r.date)?.toLocaleDateString("pt-BR") || ""}). Qualquer dúvida, é só chamar!${link}`; }
    return `Olá ${r.client_name}! 😊 Passando para lembrar sobre o pagamento pendente do documento ${r.number} no valor de ${formatBRL(r.total)}. Assim que possível, pode confirmar? Qualquer dúvida, é só chamar!${link}`;
  };

  const markPaid = async (r) => {
    setBusy(r.doc_id);
    try {
      const { data } = await api.post(`/documents/${r.doc_id}/payment-status`, { payment_status: "PAGO", base_url: window.location.origin });
      toast.success(`${r.number} marcado como PAGO`);
      const wnum = (r.client_whatsapp || "").replace(/\D/g, "");
      if (data.receipt_token && wnum) {
        const link = `${window.location.origin}/doc/${data.receipt_token}`;
        wa.trigger(r.client_whatsapp, `Olá ${r.client_name}! 😊 Recebemos o seu pagamento, muito obrigado! Segue o seu recibo: ${link}`);
      }
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro"); } finally { setBusy(""); }
  };
  const emailCharge = async (r) => {
    try { await api.post(`/documents/${r.doc_id}/email-charge`, { base_url: window.location.origin }); toast.success("E-mail de cobrança enviado"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erro ao enviar e-mail"); }
  };

  return (
    <div className="space-y-3">
      {wa.dialog}
      <Card className="p-4 border-amber-300/40 flex items-center justify-between" data-testid="billing-total-card">
        <div><p className="text-sm text-muted-foreground">Total a receber</p><p className="text-2xl font-display font-extrabold text-amber-600">{formatBRL(totalPending)}</p></div>
        <div className="h-11 w-11 rounded-xl bg-amber-500/10 text-amber-600 flex items-center justify-center"><Wallet className="h-5 w-5" /></div>
      </Card>
      {items.length === 0 ? (
        <Card className="p-10 text-center text-muted-foreground"><Wallet className="h-10 w-10 mx-auto mb-2 opacity-40" /><p>Nenhuma cobrança pendente. Documentos com pagamento PENDENTE ou PARCIAL aparecem aqui.</p></Card>
      ) : (
        <div className="space-y-2">
          {enriched.map((r) => (
            <Card key={r.doc_id} className="p-4 flex items-center gap-3 flex-wrap" data-testid={`billing-${r.doc_id}`}>
              <div className="min-w-0 flex-1">
                <p className="font-semibold truncate">{r.client_name}</p>
                <p className="text-xs text-muted-foreground truncate">{DOC_TYPES[r.type]?.label} {r.number}{r.date ? ` • vence ${r.date}` : ""}</p>
              </div>
              <Badge variant="outline" className={`text-[10px] ${r.st.tone}`} data-testid={`billing-due-${r.doc_id}`}>{r.st.label}</Badge>
              <div className="text-right shrink-0">
                <p className="font-display font-bold">{formatBRL(r.total)}</p>
                <Badge variant="outline" className={`text-[10px] ${STATUS_STYLES[r.payment_status] || ""}`}>{r.payment_status}</Badge>
              </div>
              <Button size="sm" variant="outline" disabled={busy === r.doc_id} onClick={() => markPaid(r)} data-testid={`mark-paid-${r.doc_id}`} className="text-secondary border-secondary/40 hover:bg-secondary/10">
                {busy === r.doc_id ? <Loader2 className="h-4 w-4 animate-spin" /> : <><CheckCircle2 className="h-4 w-4 mr-1" /> Pago</>}
              </Button>
              {r.client_email && <Button size="sm" variant="outline" onClick={() => emailCharge(r)} data-testid={`billing-email-${r.doc_id}`}><Mail className="h-4 w-4" /></Button>}
              <WaButton disabled={!(r.client_whatsapp || "").replace(/\D/g, "")} onClick={() => wa.trigger(r.client_whatsapp, defaultMsg(r))} testid={`billing-wa-${r.doc_id}`} />
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Reminders() {
  return (
    <div className="space-y-5">
      <div>
        <h1 className="font-display font-extrabold text-2xl sm:text-3xl flex items-center gap-2"><Bell className="h-6 w-6 text-primary" /> Lembretes</h1>
        <p className="text-muted-foreground text-sm">Follow-ups automáticos (orçamento sem resposta, pagamento, garantia, manutenção), cobranças e retornos — mensagem pronta para o WhatsApp em um toque.</p>
      </div>
      <Tabs defaultValue="followups">
        <TabsList className="grid grid-cols-3 w-full max-w-md">
          <TabsTrigger value="followups" data-testid="reminders-tab-followups">Follow-up</TabsTrigger>
          <TabsTrigger value="cobrancas" data-testid="reminders-tab-billing">Cobranças</TabsTrigger>
          <TabsTrigger value="retornos" data-testid="reminders-tab-returns">Retornos</TabsTrigger>
        </TabsList>
        <TabsContent value="followups" className="mt-4"><FollowUps /></TabsContent>
        <TabsContent value="cobrancas" className="mt-4"><Cobrancas /></TabsContent>
        <TabsContent value="retornos" className="mt-4"><Retornos /></TabsContent>
      </Tabs>
    </div>
  );
}
